import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './Users.module.scss';
import cx from 'classnames';
import { CustomButton } from '../../../../components/Button/Button';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/Icon/Icon';
import SearchComponent from '../../../../components/SearchComponent/SearchComponent';
import RecordNotFound from '../../../../components/_empty_state/RecordNotFound';
import Spinner from '../../../../../common/components/Spinner';
import Modal from '../../../../components/Modal';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import SelectOption from '../../../../components/SelectOption/SelectOption';
import uuidv4 from 'uuid/v4';
import VideoService from '../../../../service/VideoService';
import usePagination from '../../../../hooks/usePagination';
import Pagination from '../../../../components/Pagination/Pagination';
import { ToastNotification } from '../../../../components/Toast/Toast';
import { formatToDateString } from '../../../../utils/TimeUtil';
import { convertUserStatus } from '../../../../utils/userUtils';
import { useDropzone } from 'react-dropzone';
import Toggle from '../../../../components/Toggle/Toggle';
import { useUserContext } from '../../../../context/UserContext';
import { APP_USER_ROLE } from '../../../../constants/UserRoles';
import UserItem from './UserItem';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import UserFeaturesList from './UserFeaturesList';

const FILTER_OPTIONS = {
  ALL: {
    type: null,
    name: 'company.settings.users.filter.all',
  },
  ACTIVATED: {
    type: 1,
    name: 'company.settings.users.filter.activated',
  },
  GUESTS: {
    type: 2,
    name: 'company.settings.users.filter.guests',
  },
  DEACTIVATED: {
    type: 0,
    name: 'company.settings.users.filter.deactivated',
  },
};

const MAX_NAME_LENGTH = 201; // 100 for first name, 100 for last name and 1 for space

const PAGE_MARGIN_BOTTOM = 16;

const Users = ({ companyId }) => {
  const [users, setUsers] = useState([]);
  const [userMenu, setUserMenu] = useState(null);

  const [userToEdit, setUserToEdit] = useState({});
  const [userToAdd, setUserToAdd] = useState({});

  const { userRole, isSuperAdmin, isApprovalFlowEnabled, isSSOAuthenticationEnabled } = useUserContext();

  const [isShowAddNewUser, setIsShowAddNewUser] = useState(false);
  const [isShowEditUser, setIsShowEditUser] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState({ isShow: false, userId: null, name: null });
  const [isShowImportUser, setIsShowImportUser] = useState(false);

  const [touchedFields, setTouchedFields] = useState({ name: false, email: false });

  const wrapperRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [isAddToCampaign, setIsAddToCampaign] = useState(true);
  const [invalidUsers, setInvalidUsers] = useState(null);
  const [showInvalidUserMessage, setShowInvalidUserMessage] = useState(null);
  const [isLoadingImport, setIsLoadingImport] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [isListWithScroll, setIsListWithScroll] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });

  const [filterType, setFilterType] = useState(null);

  const [csvFile, setCsvFile] = useState('');
  const [csvFileName, setCsvFilename] = useState();
  const [cardClicked, setCardClicked] = useState(false);
  const [showShadow, setShowShadow] = useState(true);

  const menuRef = useRef(null);

  useOutsideClick(menuRef, () => {
    userMenu?.show && setUserMenu(null);
  });

  const availableRoles = useMemo(() => {
    const result = structuredClone(APP_USER_ROLE);
    delete result.GUEST;
    delete result.VIDEO_EDITOR;
    if (!isApprovalFlowEnabled) {
      delete result.REVIEWER;
    }
    if (!isSuperAdmin) {
      delete result.SUPER_ADMIN;
    }
    return Object.values(result);
  }, [isSuperAdmin, isApprovalFlowEnabled, APP_USER_ROLE]);

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    onDrop: acceptedFiles => {
      const file = acceptedFiles[0];
      setCsvFile(file);
      setCsvFilename(file.name);
    },
    disabled: isLoadingImport,
  });

  useEffect(() => {
    if (isDragReject) {
      ToastNotification({
        message: `${t('general.toast.import_not_succes')}`,
        isMobileView: isMobileView,
      });
    }
  }, [isDragReject]);

  const userFilters = useMemo(() => {
    switch (userRole) {
      case APP_USER_ROLE.SUPER_ADMIN.TYPE: {
        return Object.values(FILTER_OPTIONS);
      }
      case APP_USER_ROLE.MANAGER.TYPE: {
        return [FILTER_OPTIONS.ALL, FILTER_OPTIONS.GUESTS];
      }
      default: {
        return [];
      }
    }
  }, [userRole]);

  const getUsers = useCallback(
    async params => {
      try {
        const response = await VideoService.getCompanyUsers(params, companyId);
        return response;
      } catch (err) {
        console.log(err);
      }
    },
    [companyId]
  );

  const {
    list: usersList,
    query,
    totalData,
    isLoading: isUsersLoading,
    onSearch,
    onChangePage,
    onChangeFilter,
    onSort,
    loadList,
    onResetQuery,
  } = usePagination({
    service: getUsers,
    itemsPerPage: 10,
  });

  const usersRef = useRef(null);

  const modalRef = useRef(null);
  const editUserRef = useRef(null);

  const { t } = useTranslation();

  const checkFieldValidation = useCallback(
    fieldName => {
      if (isShowAddNewUser) {
        const { name, email } = userToAdd;

        if (fieldName === 'name' && touchedFields.name) {
          return name?.length > 0;
        }

        if (fieldName === 'email' && touchedFields.email) {
          const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm;
          return re.test(email?.toLowerCase());
        }
      }

      if (isShowEditUser) {
        const { name, email } = userToEdit;

        if (fieldName === 'name' && touchedFields.name) {
          return name?.length > 0;
        }

        if (fieldName === 'email' && touchedFields.email) {
          const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm;
          return re.test(email?.toLowerCase());
        }
      }

      return true;
    },
    [userToEdit, userToAdd, isShowAddNewUser, isShowEditUser, touchedFields]
  );

  const isEveryFieldValid = useMemo(() => {
    let user = {};

    if (isShowAddNewUser) {
      user = userToAdd;
    }

    if (isShowEditUser) {
      user = userToEdit;
    }

    const { name, email, role } = user;
    const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm;

    const isNameValid = name?.length > 0;
    const isEmailValid = re.test(email?.toLowerCase());
    const isRoleValid = !!role;

    return isNameValid && isEmailValid && isRoleValid;
  }, [userToAdd, userToEdit, isShowAddNewUser, isShowEditUser]);

  const isUserToEditChanged = useMemo(() => {
    if (userToEdit) {
      const { name, email, role } = users?.find(({ username }) => username === userToEdit?.username) || {};

      const isNameChanged = name !== userToEdit?.name;
      const isEmailChanged = email !== userToEdit?.email;
      const isRoleChanged = role !== userToEdit?.role;

      return isNameChanged || isEmailChanged || isRoleChanged;
    }
  }, [userToEdit]);

  const getSortIcon = useCallback(
    type => {
      if (type === query.sortBy) {
        if (query.sortOrder === 'asc') {
          return 'sortTop';
        }

        if (query.sortOrder === 'desc') {
          return 'sortBottom';
        }
      }

      return 'sort';
    },
    [query]
  );

  const handleClickExport = async () => {
    try {
      setIsLoadingExport(true);
      const response = await VideoService.getUserListAsCsv(companyId);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      setIsLoadingExport(false);
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_list.csv');

      // Simulate a click on the link to trigger the download
      document.body.appendChild(link);
      link.click();
      ToastNotification({
        message: `${t('general.toast.export_success')}`,
        iconName: 'success',
        isMobileView: isMobileView,
      });
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response;
    } catch (err) {
      ToastNotification({
        message: `${t('general.toast.export_failed')}`,
        isMobileView: isMobileView,
      });
      setIsLoadingExport(false);
      console.log(err);
    }
  };

  const handleShowImportUser = () => {
    setIsShowImportUser(true);
  };

  const handleCloseImportUser = () => {
    setIsShowImportUser(false);
    setCsvFile('');
    setCsvFilename('');
  };

  const handleShowInvalidUserMessage = () => {
    setShowInvalidUserMessage(false);
  };

  const downloadCSV = () => {
    const csv = 'name,email,role\nUser Name,user@test.com,app';
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'import.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleAddToCampaign = event => {
    const checked = event.target.checked;
    setIsAddToCampaign(checked);
  };

  const toggleAddSingleUserToCampaign = event => {
    updateUserToAddInfo({
      addToDefaultCampaign: event.target.checked,
    });
  };

  const handleClickImportUsersFromCsv = async () => {
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const params = isAddToCampaign ? { addToDefaultCampaign: 'true' } : {};
      setIsLoadingImport(true);
      const response = await VideoService.importUsersFromCsv(formData, params, companyId);
      setIsLoadingImport(false);
      setIsShowImportUser(false);

      if (response?.invalidUsers.length > 0) {
        setShowInvalidUserMessage(true);
        setInvalidUsers(response?.invalidUsers);
      }
      if (response?.createdUsers.length === 0) {
        ToastNotification({
          message: `${t('general.toast.import_no_user_created')}`,
          isMobileView: isMobileView,
        });
      } else {
        ToastNotification({
          message: `${t('general.toast.import_success')}`,
          iconName: 'success',
          isMobileView: isMobileView,
        });
      }
      setCsvFile('');
      setCsvFilename('');
      return response;
    } catch (err) {
      setCsvFile('');
      setCsvFilename('');
      setIsLoadingImport(false);
      setIsShowImportUser(false);

      ToastNotification({
        message: `${t('general.toast.error_parsing')}`,
        isMobileView: isMobileView,
      });
      console.log(err);
    }
  };

  useEffect(() => {
    if (!isShowImportUser) {
      setInvalidUsers(null);
      setIsLoadingImport(false);
    }
  }, [isShowImportUser]);

  const handleFilter = type => {
    setFilterType(type);
    onChangeFilter(type);
  };

  const handleResetPassword = async userId => {
    try {
      setIsLoading(true);
      await VideoService.resetPasswordForCompanyUser(userId, companyId);

      ToastNotification({
        message: t('company.settings.users.toast.reset_password.succeed'),
        iconName: 'success',
      });
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.users.toast.reset_password.error'),
      });
    }

    setIsLoading(false);
  };

  const handleResendInvite = async userId => {
    try {
      setIsLoading(true);
      await VideoService.resendCompanyUserInvite(userId, companyId);

      ToastNotification({
        message: t('company.settings.users.toast.resend_invite.succeed'),
        iconName: 'success',
      });
    } catch (err) {
      let errorMessage = t('company.settings.users.toast.resend_invite.error');

      if (err.response.status === 403) {
        errorMessage = t('company.settings.users.toast.resend_invite.error_user_invalid_state');
      }

      console.log(err);
      ToastNotification({
        message: errorMessage,
      });
    }

    setIsLoading(false);
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      if (isSuperAdmin) {
        await VideoService.deleteCompanyUser(showDeleteConfirmation.userId, companyId);
      } else {
        await VideoService.deactivateCompanyUser(showDeleteConfirmation.userId, companyId);
      }
      await loadList(true);
      ToastNotification({
        message: t('company.settings.users.toast.delete.succeed'),
        iconName: 'success',
      });
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.users.toast.delete.error'),
      });
    }

    setIsLoading(false);
    handleCloseDeleteConfirmation();
  };

  const handleSort = field => {
    if (query.sortBy === field) {
      onSort({ sortBy: field, sortOrder: query.sortOrder === 'asc' ? 'desc' : 'asc' });
      return;
    }

    onSort({ sortBy: field, sortOrder: 'asc' });
  };

  const handleShowAddNewUser = () => {
    setIsShowAddNewUser(true);
  };

  const handleCloseAddNewUser = () => {
    setIsShowAddNewUser(false);
  };

  const handleShowEditUser = async () => {
    setIsShowEditUser(true);
    try {
      setIsUserDetailsLoading(true);
      const { status, createdAt, updatedAt, lastSeen } = await VideoService.getCompanyUser(userToEdit.username, companyId);
      setUserToEdit(prevState => ({ ...prevState, status, createdAt, updatedAt, lastSeen }));
    } catch (err) {
      console.log(err);
    }

    setIsUserDetailsLoading(false);
  };

  const handleToggleUser = async (e, username, enabled) => {
    e.stopPropagation();
    setUserMenu(null);
    setIsLoading(true);
    try {
      enabled
        ? await VideoService.deactivateCompanyUser(username, companyId)
        : await VideoService.activateCompanyUser(username, companyId);
      await loadList(true);
      ToastNotification({
        message: t('company.settings.users.toast.update.succeed'),
        iconName: 'success',
      });
    } catch (e) {
      console.log('error', e);
      ToastNotification({
        message: t('company.settings.users.toast.update.error'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseEditUser = () => {
    setIsShowEditUser(false);
  };

  const handleCloseDeleteConfirmation = () => {
    setShowDeleteConfirmation({ isShow: false, userId: null, name: null });
  };

  const updateUserToEditInfo = changes => {
    setUserToEdit(prevState => ({ ...prevState, ...changes }));
  };

  const updateUserToAddInfo = changes => {
    setUserToAdd(prevState => ({ ...prevState, ...changes }));
  };

  const clearUserToEditInfo = () => {
    setUserToEdit({});
  };

  const clearUserToAddInfo = () => {
    setUserToAdd({});
  };

  const handleUserCardClicked = username => {
    const selectedUser = users?.find(item => item.username === username) || {};
    updateUserToEditInfo(selectedUser);
    setCardClicked(true);
  };

  const handleDotsBtn = (e, user) => {
    e.stopPropagation();
    setUserMenu({ show: true, user });

    let left = e.clientX;
    let top = e.clientY;
    if (window.innerHeight - top < 160) {
      top -= 150;
    }
    setMenuPosition({ left, top, maxWidth: window.innerWidth - left - 20, width: 'minContent' });
  };

  const handleMenuItem = (e, type = null, user) => {
    setUserMenu(null);
    e.stopPropagation();
    switch (type) {
      case 'delete':
        setShowDeleteConfirmation({ isShow: true, userId: user?.username, name: user?.name });
        break;
      case 'reset':
        handleResetPassword(user.username);
        break;
      case 'resend':
        handleResendInvite(user.username);
        break;
      default:
        handleShowEditUser(true);
        break;
    }
  };

  const handleSaveEdits = async () => {
    const payload = {
      userFullName: userToEdit.name,
      userEmail: userToEdit.email,
      userRole: userToEdit.role,
    };

    try {
      setIsLoading(true);
      await VideoService.editCompanyUser(userToEdit.username, payload, companyId);
      await loadList(true);
      handleCloseEditUser();
      ToastNotification({
        message: t('company.settings.users.toast.update.succeed'),
        iconName: 'success',
      });
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t(err.response.status === 409 ? 'company.settings.users.toast.update.conflict' : 'company.settings.users.toast.update.error', { email: userToEdit.email }),
      });
    }

    setIsLoading(false);
  };

  const handleSaveAddedUser = async () => {
    const payload = {
      userFullName: userToAdd.name,
      userEmail: userToAdd.email,
      userRole: userToAdd.role,
      addToDefaultCampaign: userToAdd.addToDefaultCampaign,
    };

    try {
      setIsLoading(true);
      await VideoService.createCompanyUser(payload, companyId);
      await loadList(true);
      handleCloseAddNewUser();
      ToastNotification({
        message: t('company.settings.users.toast.create.succeed'),
        iconName: 'success',
      });
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: `${t('company.settings.users.toast.create.error')}. ${err?.response?.data?.message}`,
      });
    }

    setIsLoading(false);
  };

  const handleOnListScroll = e => {
    setShowShadow({
      top: e.currentTarget.scrollTop > 0,
      bottom: e.currentTarget.scrollHeight - e.currentTarget.scrollTop > e.currentTarget.offsetHeight,
    });
  };

  useEffect(() => {
    if (userMenu?.user) {
      const selectedUser = users?.find(({ username }) => username === userMenu?.user?.username) || {};
      updateUserToEditInfo(selectedUser);
    }
  }, [userMenu]);

  useEffect(() => {
    if (isShowAddNewUser) {
      setUserToAdd(prevState => ({ ...prevState, username: uuidv4(), role: APP_USER_ROLE.CREATOR.TYPE, addToDefaultCampaign: true }));
    } else {
      setTouchedFields({ name: false, email: false });
      clearUserToAddInfo();
    }
  }, [isShowAddNewUser]);

  useEffect(() => {
    if (!isShowEditUser) {
      setTouchedFields({ name: false, email: false });
      clearUserToEditInfo();
    }
  }, [isShowEditUser]);

  useEffect(() => {
    setUsers(usersList);
  }, [usersList]);

  useEffect(() => {
    if (cardClicked) {
      handleShowEditUser();
      setCardClicked(false);
    }
  }, [cardClicked]);

  useEffect(() => {
    onResetQuery();
  }, [companyId]);

  useEffect(() => {
    if (wrapperRef?.current && !isUsersLoading) {
      // Wait until transition will over to define the position
      setTimeout(() => {
        const { top } = wrapperRef.current.getBoundingClientRect();
        wrapperRef.current.style.maxHeight = `${Math.round(window.innerHeight - top) - PAGE_MARGIN_BOTTOM}px`;
        setIsListWithScroll(usersRef?.current?.scrollHeight > usersRef?.current?.offsetHeight);
      }, 250);
    }
  }, [wrapperRef.current, window.screen.height, isUsersLoading]);

  useEffect(() => {
    usersRef.current.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [query]);

  const UserFilters = filters => {
    if (!filters) {
      return null;
    }

    return (
      <ul className={styles.enabledFiltersContainer}>
        {filters.map(({ name, type }) => (
          <li
            key={name}
            className={cx(styles.filterItem, { [styles.active]: type === filterType })}
            onClick={() => handleFilter(type)}
          >
            <span>{t(name)}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.header}>
        <div className={styles.headerTitleContainer}>
          <h2 className={styles.headerTitle}>{t('company.settings.users.company_users')}</h2>
          {userFilters && UserFilters(userFilters)}
        </div>
        <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
      </div>
      <div
        className={cx(styles.usersTable, {
          [styles.showBottomShadow]: isListWithScroll && showShadow.bottom,
          [styles.showTopShadow]: isListWithScroll && showShadow.top,
        })}
      >
        <div className={styles.userTableHeader}>
          <div className={styles.nameItemHeader} onClick={() => handleSort('name')}>
            <span>{t('company.settings.users.name')}</span>
            <Icon src={getSortIcon('name')} verticalAlign="middle" className={styles.sortIcon} />
          </div>
          <div className={styles.emailItemHeader} onClick={() => handleSort('email')}>
            <span>{t('company.settings.users.email')}</span>
            <Icon src={getSortIcon('email')} verticalAlign="middle" className={styles.sortIcon} />
          </div>
          <div className={styles.lastSeenItemHeader} onClick={() => handleSort('last_seen')}>
            <span>{t('company.settings.users.last_seen')}</span>
            <Icon src={getSortIcon('last_seen')} verticalAlign="middle" className={styles.sortIcon} />
          </div>
          <div className={styles.emptyItemHeader} />
        </div>
        <div ref={usersRef} className={cx(styles.userRows, { [styles.withScroll]: isListWithScroll })} onScroll={handleOnListScroll}>
          {users.length
            ? users.map(user => (
              <UserItem
                key={user.username}
                user={user}
                isUsersLoading={isUsersLoading}
                querySearch={query.search}
                handleUserCardClicked={handleUserCardClicked}
                handleDotsBtn={handleDotsBtn}
              />
            ))
            : null}
          {isUsersLoading && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
          {usersList.length === 0 && query.search && !isUsersLoading && <RecordNotFound searchTerm={query.search} />}
        </div>
      </div>
      <div className={styles.usersButtonsContainer}>
        {!isSSOAuthenticationEnabled && (
          <div className={styles.usersButtons}>
            <CustomButton
              label={t('company.settings.users.create_new_user')}
              variant="primary"
              onClick={handleShowAddNewUser}
              className={styles.userButton}
              loadingSpinner={true}
              disabled={isUsersLoading}
            />
            <CustomButton
              label={
                <>
                  <Icon src="importUsers" verticalAlign="middle" className={styles.icon} />
                  <span>{t('company.settings.users.import')}</span>
                </>
              }
              variant="secondary"
              className={styles.userButton}
              loadingSpinner={true}
              disabled={isUsersLoading}
              onClick={() => handleShowImportUser()}
            />
            <CustomButton
              label={
                <>
                  <Icon src="exportUsers" verticalAlign="middle" className={styles.icon} />
                  <span>{t('company.settings.users.export')}</span>
                </>
              }
              variant="secondary"
              className={styles.userButton}
              loadingSpinner={true}
              loading={isLoadingExport}
              loadingText={t('company.settings.users.export')}
              onClick={() => handleClickExport()}
              disabled={isUsersLoading}
            />
          </div>
        )}
        {!!totalData && (
          <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={onChangePage} />
        )}
      </div>

      {userMenu?.show && (
        <ul className={styles.menu} ref={menuRef} style={menuPosition}>
          <li className={styles.menuItem} onClick={handleMenuItem}>
            {t('general.edit')}
          </li>
          {userMenu.user.lastSeen !== null && !isSSOAuthenticationEnabled && userMenu.user.enabled && (
            <li
              className={cx(styles.menuItem, { [styles.menuItemDisabled]: isLoading })}
              onClick={e => handleMenuItem(e, 'reset', userMenu.user)}
            >
              {t('company.settings.users.reset_password')}
            </li>
          )}
          {userMenu.user.lastSeen === null && !isSSOAuthenticationEnabled && (
            <li
              className={cx(styles.menuItem, { [styles.menuItemDisabled]: isLoading })}
              onClick={e => handleMenuItem(e, 'resend', userMenu.user)}
            >
              {t('company.settings.users.resend_invite')}
            </li>
          )}
          <li className={styles.menuItem} onClick={e => handleMenuItem(e, 'delete', userMenu.user)}>
            {t('general.video_action_button.delete')}
          </li>
          {isSuperAdmin && (
            <li
              className={cx(styles.menuItem, { [styles.menuItemDisabled]: isLoading })}
              onClick={e => handleToggleUser(e, userMenu.user?.username, userMenu.user.enabled)}
            >
              {t(userMenu.user.enabled ? 'company.settings.users.deactivate' : 'company.settings.users.activate')}
            </li>
          )}
        </ul>
      )}

      <Modal innerRef={modalRef} className={styles.modal} onClose={handleCloseAddNewUser} open={isShowAddNewUser}>
        <div className={styles.modalContainer}>
          <div className={styles.headerModal}>
            <span className={styles.modalTitle}>{t('company.settings.users.add_new_user')}</span>
            <CloseButton onClose={handleCloseAddNewUser} />
          </div>
          <div className={styles.modalContent}>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('company.settings.users.name')}</span>
              <input
                type="text"
                className={cx(styles.infoInput, { [styles.invalidInput]: !checkFieldValidation('name') })}
                value={userToAdd.name}
                placeholder={t('company.settings.users.fill_in_name')}
                onChange={event => updateUserToAddInfo({ name: event.target?.value.trimStart() })}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, name: true }))}
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('company.settings.users.email_address')}</span>
              <input
                type="text"
                className={cx(styles.infoInput, { [styles.invalidInput]: !checkFieldValidation('email') })}
                value={userToAdd.email}
                placeholder={t('company.settings.users.fill_in_email_address')}
                onChange={event => updateUserToAddInfo({ email: event.target?.value.trimStart() })}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, email: true }))}
              />
            </div>
            <SelectOption
              windowRef={modalRef}
              title={t('company.settings.users.role')}
              currentlySelected={availableRoles.find(({ TYPE }) => TYPE === userToAdd.role)}
              options={availableRoles}
              onSelect={role => updateUserToAddInfo({ role: role.TYPE })}
              className={styles.selectOption}
            />
            <div className={styles.toggleContainer}>
              <Toggle
                id={t('company.settings.users.add_user_to_default_campaign')}
                size={'medium'}
                checked={userToAdd.addToDefaultCampaign}
                onChange={toggleAddSingleUserToCampaign}
              />
              <span>{t('company.settings.users.add_user_to_default_campaign')}</span>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <CustomButton variant="outline" label={t('general.cancel')} onClick={handleCloseAddNewUser} />
            <CustomButton
              className={styles.saveButtons}
              label={t('company.settings.users.invite_user')}
              variant="primary"
              disabled={!isEveryFieldValid}
              onClick={handleSaveAddedUser}
              loading={isLoading}
              loadingSpinner={true}
            />
          </div>
        </div>
      </Modal>

      <Modal className={styles.modal} onClose={handleCloseEditUser} open={isShowEditUser}>
        <div ref={editUserRef} className={styles.modalContainer}>
          <div className={styles.headerModal}>
            <span className={styles.modalTitle}>{t('company.settings.users.edit_user')}</span>
            <CloseButton onClose={handleCloseEditUser} />
          </div>
          <div className={styles.modalContent}>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('company.settings.users.name')}</span>
              <input
                type="text"
                className={cx(styles.infoInput, { [styles.invalidInput]: !checkFieldValidation('name') })}
                value={userToEdit.name}
                placeholder={t('company.settings.users.fill_in_name')}
                onChange={event => updateUserToEditInfo({ name: event.target?.value.trimStart() })}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, name: true }))}
                maxLength={MAX_NAME_LENGTH}
              />
            </div>
            <div className={styles.inputContainer}>
              <span className={styles.inputHeader}>{t('company.settings.users.email_address')}</span>
              <input
                type="text"
                className={cx(styles.infoInput, { [styles.invalidInput]: !checkFieldValidation('email') })}
                value={userToEdit.email}
                placeholder={t('company.settings.users.fill_in_email_address')}
                onChange={event => updateUserToEditInfo({ email: event.target?.value.trimStart() })}
                onBlur={() => setTouchedFields(prevState => ({ ...prevState, email: true }))}
              />
            </div>
            <SelectOption
              parentRef={editUserRef}
              title={t('company.settings.users.role')}
              currentlySelected={availableRoles.find(({ TYPE }) => TYPE === userToEdit.role)}
              options={availableRoles}
              onSelect={role => updateUserToEditInfo({ role: role.TYPE })}
              currentInputClassName={styles.currentSelect}
              className={styles.selectOption}
            />
            <div className={styles.userDetailsContainer}>
              {isUserDetailsLoading ? (
                <Spinner width="40px" height="40px" size="4px" className={styles.spinnerEdit} />
              ) : (
                <>
                  <div className={styles.detailContainer}>
                    <span className={styles.detailTitle}>{t('company.settings.users.status')}:</span>
                    <span className={styles.detailText}>{t(convertUserStatus(userToEdit.status))}</span>
                  </div>
                  <div className={styles.detailContainer}>
                    <span className={styles.detailTitle}>{t('company.settings.users.created_at')}:</span>
                    <span className={styles.detailText}>{formatToDateString(userToEdit.createdAt) || '-'}</span>
                  </div>
                  <div className={styles.detailContainer}>
                    <span className={styles.detailTitle}>{t('company.settings.users.updated_at')}:</span>
                    <span className={styles.detailText}>{formatToDateString(userToEdit.updatedAt) || '-'}</span>
                  </div>
                  <div className={styles.detailContainer}>
                    <span className={styles.detailTitle}>{t('company.settings.users.last_seen')}:</span>
                    <span className={styles.detailText}>{formatToDateString(userToEdit.lastSeen) || '-'}</span>
                  </div>
                  {/* User-overridable features list */}
                  {userToEdit.username && companyId && (
                    <UserFeaturesList userId={userToEdit.username} companyId={companyId} />
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <CustomButton variant="outline" label={t('general.cancel')} onClick={handleCloseEditUser} />
            <CustomButton
              className={styles.saveButtons}
              label={t('company.settings.users.save_edits')}
              variant="primary"
              disabled={!isEveryFieldValid || !isUserToEditChanged}
              onClick={handleSaveEdits}
              loading={isLoading}
              loadingSpinner={true}
            />
          </div>
        </div>
      </Modal>
      <Modal className={styles.modal} onClose={handleCloseDeleteConfirmation} open={showDeleteConfirmation.isShow}>
        <div className={styles.modalContent}>
          <div className={styles.headerModal}>
            <span className={styles.modalDeleteTitle}>
              {t('company.settings.users.delete_modal.title', { name: showDeleteConfirmation.name })}
            </span>
            <CloseButton className={styles.closeBtn} onClose={handleCloseDeleteConfirmation} />
          </div>

          <span className={styles.modalDeleteText}>{t('company.settings.users.delete_modal.text')}</span>
          <div className={styles.modalFooter}>
            <CustomButton variant="outline" label={t('general.cancel')} onClick={handleCloseDeleteConfirmation} />
            <CustomButton
              className={styles.saveButtons}
              label={t('company.settings.users.delete_modal.button_text')}
              variant="primary"
              onClick={handleDelete}
              loading={isLoading}
              loadingSpinner={true}
            />
          </div>
        </div>
      </Modal>

      <Modal className={styles.modal} onClose={handleCloseImportUser} open={isShowImportUser}>
        <div ref={editUserRef} className={cx(styles.modalContainer, { [styles.loadingContainer]: isLoadingImport })}>
          <div className={styles.headerModal}>
            <span className={styles.modalTitle}>{t('company.settings.users.import_title')}</span>
            <CloseButton onClose={handleCloseImportUser} />
          </div>
          <>
            <div className={styles.modalContent}>
              <div className={styles.inputContainer}>
                <p className={styles.inputHeader}>{t('company.settings.users.import_description')}</p>
                <CustomButton
                  className={styles.downloadButton}
                  label={t('company.settings.users.import_download_example')}
                  variant="outline"
                  onClick={downloadCSV}
                />
              </div>

              <div {...getRootProps()} className={styles.dropzoneWrapper}>
                <input {...getInputProps()} accept="csv" />
                {csvFileName ? (
                  <>
                    <Icon src="csvFile" verticalAlign="middle" className={styles.csvIcon} />
                    <p> {csvFileName}</p>
                  </>
                ) : (
                  <p>{t('company.settings.users.import_dropzone')}</p>
                )}
              </div>
            </div>

            <div className={styles.toggleContainer}>
              <Toggle
                id={t('company.settings.users.import_checkbox')}
                size={'medium'}
                checked={isAddToCampaign}
                onChange={toggleAddToCampaign}
                disabled={isLoadingImport}
              ></Toggle>
              <span>{t('company.settings.users.import_checkbox')}</span>
            </div>
          </>
          <div className={styles.modalFooter}>
            <CustomButton variant="outline" label={t('general.cancel')} onClick={handleCloseImportUser} />
            <CustomButton
              label={t('company.settings.users.import_save')}
              variant="primary"
              disabled={!csvFile || isDragReject}
              loading={isLoadingImport}
              loadingSpinner={true}
              loadingText={t('company.settings.users.import_save')}
              onClick={handleClickImportUsersFromCsv}
            />
          </div>
        </div>
      </Modal>

      <Modal className={styles.modal} onClose={handleShowInvalidUserMessage} open={showInvalidUserMessage}>
        <div ref={editUserRef} className={styles.modalContainer}>
          <div className={styles.modalInvalidUser}>
            <div className={styles.headerModal}>
              <span className={styles.modalTitle}>{t('company.settings.users.import_title')}</span>
              <CloseButton onClose={handleShowInvalidUserMessage} />
            </div>

            <div className={styles.invalidContent}>
              <p className={styles.invalidTitle}>{t('company.settings.users.import_error')}</p>
              <p className={styles.invalidUsers}>
                {invalidUsers?.map((user, index) => (
                  <span key={index}>
                    {user}
                    {index === invalidUsers.length - 1 ? '' : <span>,&nbsp;</span>}
                  </span>
                ))}
              </p>
            </div>

            <div className={styles.modalFooter}>
              <CustomButton label={t('general.continue')} variant="primary" onClick={handleShowInvalidUserMessage} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
